import React, { useMemo, useCallback, useRef, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, themeAlpine, AllCommunityModule, ModuleRegistry, RowClickedEvent, RowClassParams } from 'ag-grid-community';
import { AlternateMaterial } from '../types/CommonTypes';
import { useAssignment } from '../hooks/useAssignment';

ModuleRegistry.registerModules([AllCommunityModule]);

const SELECTED_ROW_BG = '#d0e8ff';

const STYLES = {
    container: { width: '100%', marginTop: 8 } as React.CSSProperties,
    labelRow: { fontWeight: 600, fontSize: '0.85em', marginBottom: 4, color: '#39444a', display: 'flex', alignItems: 'center', gap: 8 } as React.CSSProperties,
    selectedHint: { fontWeight: 400, color: '#005DB4', fontSize: '0.9em' } as React.CSSProperties,
    deleteAllBtn: { fontSize: '0.75em', padding: '1px 4px', cursor: 'pointer', background: 'transparent', border: '1px solid #c00', color: '#c00', borderRadius: 3 } as React.CSSProperties,
    deleteRowBtn: { padding: '0 6px', cursor: 'pointer', fontSize: '0.8em', background: 'transparent', border: 'none', color: '#c00' } as React.CSSProperties,
};

// Header component for the delete column — renders a "✕ All" button.
function DeleteAllHeader({ onDeleteAll }: { onDeleteAll: () => void }) {
    return (
        <button
            onClick={onDeleteAll}
            style={STYLES.deleteAllBtn}
            title="Remove all alternate materials"
        >
            ✕ All
        </button>
    );
}

interface Props {
    onDeselect?: () => void;
    /** Optional ref to limit the click-outside scope. Only clicks within this element trigger deselect. */
    scopeRef?: React.RefObject<HTMLElement | null>;
}

export default function AlternateTable({ onDeselect, scopeRef }: Props): JSX.Element {
    const { alternateMaterials, selectedAlternateId, selectAlternate, removeAlternate, clearAllAlternates } = useAssignment();
    const containerRef = useRef<HTMLDivElement>(null);

    // Deselect when clicking inside the scoped area but outside the table.
    useEffect(() => {
        if (!selectedAlternateId) return;
        const handleMouseDown = (e: MouseEvent) => {
            const target = e.target as Node;
            const insideScope = scopeRef ? scopeRef.current?.contains(target) : true;
            const insideTable = containerRef.current?.contains(target);
            if (insideScope && !insideTable) {
                selectAlternate(null);
                onDeselect?.();
            }
        };
        document.addEventListener('mousedown', handleMouseDown);
        return () => document.removeEventListener('mousedown', handleMouseDown);
    }, [selectedAlternateId, selectAlternate, onDeselect, scopeRef]);

    const handleDeleteRow = useCallback((id: string) => {
        if (id === selectedAlternateId) {
            selectAlternate(null);
            onDeselect?.();
        }
        removeAlternate(id);
    }, [selectedAlternateId, selectAlternate, onDeselect, removeAlternate]);

    const handleDeleteAll = useCallback(() => {
        if (selectedAlternateId) {
            selectAlternate(null);
            onDeselect?.();
        }
        clearAllAlternates();
    }, [selectedAlternateId, selectAlternate, onDeselect, clearAllAlternates]);

    const columnDefs: ColDef<AlternateMaterial>[] = useMemo(() => [
        { headerName: 'Part Nr', field: 'partNr', flex: 70 } as ColDef<AlternateMaterial>,
        { headerName: 'Alternate Base Material', field: 'materialName', flex: 120 } as ColDef<AlternateMaterial>,
        {
            headerName: 'Condition',
            flex: 80,
            valueGetter: (params) => {
                const row = params.data;
                if (!row?.condition) return '';
                return row.condition.code ?? '';
            },
        } as ColDef<AlternateMaterial>,
        {
            // Delete All button in header; individual ✕ per row
            headerComponent: DeleteAllHeader,
            headerComponentParams: { onDeleteAll: handleDeleteAll },
            flex: 30,
            cellRenderer: (params: any) => {
                const id: string = params.data?.id;
                return (
                    <button
                        style={STYLES.deleteRowBtn}
                        title="Remove alternate"
                        onClick={(e) => { e.stopPropagation(); handleDeleteRow(id); }}
                    >
                        ✕
                    </button>
                );
            },
        } as ColDef<AlternateMaterial>,
    ], [handleDeleteRow, handleDeleteAll]);

    const handleRowClicked = (event: RowClickedEvent<AlternateMaterial>) => {
        const id = event.data?.id ?? null;
        if (id) selectAlternate(id);
    };

    const getRowStyle = (params: RowClassParams<AlternateMaterial>): React.CSSProperties | undefined => {
        if (params.data?.id === selectedAlternateId) {
            return { background: SELECTED_ROW_BG };
        }
        return undefined;
    };

    return (
        <div ref={containerRef} style={STYLES.container}>
            <div style={STYLES.labelRow}>
                <span>Alternate Base Materials</span>
                {selectedAlternateId && (
                    <span style={STYLES.selectedHint}>
                        — row selected, assign a condition from the Dimensions/Colors tab
                    </span>
                )}
            </div>
            <AgGridReact<AlternateMaterial>
                rowData={alternateMaterials}
                columnDefs={columnDefs}
                domLayout="autoHeight"
                theme={themeAlpine.withParams({
                    spacing: '2px',
                    columnBorder: '1px solid #adb8c2f6',
                    headerColumnBorder: '1px solid #adb8c2f6',
                    wrapperBorder: '1px solid #adb8c2f6',
                    wrapperBorderRadius: '8px',
                    rowBorder: '1px solid #adb8c2f6',
                })}
                onRowClicked={handleRowClicked}
                getRowStyle={getRowStyle}
            />
        </div>
    );
}
